import { Link, useRouter, useRouterState } from '@tanstack/react-router';
import {
  ArrowLeft,
  Bell,
  Clapperboard,
  CreditCard,
  LayoutGrid,
  LifeBuoy,
  Menu,
  MessageSquare,
  Monitor,
  Radio,
  Search,
  Settings,
  Sparkles,
  Tag,
  Trophy,
  Video,
  X,
} from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { cn, Button, PixlideLogo } from '@pixlide/ui';
import { useAuthStore } from '@/stores/auth-store';
import { useLogout } from '@/api/queries/auth';
import { useNotifications, useSubscription } from '@/api/queries/dashboard';
import { PlanUsageCard } from '@/components/layout/PlanUsageCard';
import { Toaster } from '@/components/layout/Toaster';
import { useNotificationAlerts } from '@/components/layout/useNotificationAlerts';
import { usePreferencesStore } from '@/stores/preferences-store';

interface NavEntry {
  to: string;
  label: string;
  icon: ReactNode;
  /** Permission requise pour afficher l'entrée. */
  permission?: string;
}

/**
 * Navigation, reprise de la maquette du board produit.
 *
 * Chaque entrée déclare la permission qui la conditionne. Point important : c'est du
 * CONFORT D'INTERFACE, pas de la sécurité — masquer un lien n'empêche personne d'appeler
 * l'API directement. L'autorisation réelle est appliquée deux fois côté serveur (endpoint
 * FastEndpoints et behavior d'autorisation du pipeline CQRS).
 */
const primaryNav: NavEntry[] = [
  { to: '/', label: 'Tableau de bord', icon: <LayoutGrid className="size-4.5" /> },
  { to: '/creer', label: 'Créer une vidéo', icon: <Sparkles className="size-4.5" />, permission: 'projects:write' },
  { to: '/videos', label: 'Mes vidéos', icon: <Video className="size-4.5" />, permission: 'projects:read' },
  { to: '/diffusion', label: 'Diffusion', icon: <Radio className="size-4.5" />, permission: 'broadcasts:read' },
  { to: '/ecrans', label: 'Écrans', icon: <Monitor className="size-4.5" />, permission: 'screens:read' },
  { to: '/regie', label: 'Régie', icon: <Clapperboard className="size-4.5" />, permission: 'screens:write' },
];

const secondaryNav: NavEntry[] = [
  { to: '/offres', label: 'Nos offres', icon: <Tag className="size-4.5" /> },
  { to: '/messages', label: 'Messages', icon: <MessageSquare className="size-4.5" /> },
  { to: '/transactions', label: 'Transactions', icon: <CreditCard className="size-4.5" />, permission: 'billing:read' },
  { to: '/support', label: 'Support', icon: <LifeBuoy className="size-4.5" /> },
  { to: '/parametres', label: 'Paramètres', icon: <Settings className="size-4.5" /> },
  { to: '/exploits', label: 'Mes exploits', icon: <Trophy className="size-4.5" /> },
];

/**
 * Libellé du fil d'Ariane : celui de l'entrée de navigation dont le chemin est un préfixe de
 * la page courante, en préférant le préfixe le plus long (`/creer/sur-mesure` doit remonter
 * vers « Créer une vidéo », pas vers un libellé générique).
 */
const breadcrumbNav = [...primaryNav, ...secondaryNav];

function useBreadcrumbLabel(pathname: string): string | null {
  if (pathname === '/') return null;

  return breadcrumbNav
    .filter((entry) => entry.to !== '/' && pathname.startsWith(entry.to))
    .sort((a, b) => b.to.length - a.to.length)[0]?.label ?? null;
}

function NavList({
  entries,
  permissions,
  onNavigate,
}: {
  entries: NavEntry[];
  permissions: readonly string[];
  /** Appelé après un clic sur un lien : ferme le tiroir mobile, no-op sur desktop. */
  onNavigate?: () => void;
}) {
  const visible = entries.filter(
    (entry) => entry.permission === undefined || permissions.includes(entry.permission),
  );

  return (
    <ul className="flex flex-col gap-0.5">
      {visible.map((entry) => (
        <li key={entry.to}>
          <Link
            to={entry.to}
            onClick={onNavigate}
            className="group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-ink-500 transition hover:bg-ink-50 hover:text-ink-900"
            // `activeProps` de TanStack Router : l'état actif vient du routeur, il ne peut
            // donc pas se désynchroniser de l'URL réelle.
            activeProps={{ className: 'bg-ink-50 font-medium text-ink-900' }}
            activeOptions={{ exact: entry.to === '/' }}
          >
            <span aria-hidden className="text-ink-300 group-hover:text-ink-700">
              {entry.icon}
            </span>
            {entry.label}
          </Link>
        </li>
      ))}
    </ul>
  );
}

/**
 * Coquille applicative : barre latérale, barre supérieure, zone de contenu.
 *
 * Rendue une seule fois pour toute l'application (route racine du routeur) : la navigation
 * ne re-monte donc pas à chaque changement de page, ce qui préserve l'état des champs de
 * recherche et évite un clignotement à chaque clic.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const router = useRouter();
  const user = useAuthStore((state) => state.user);
  const logout = useLogout();
  // Relevé plus fréquent quand l'utilisateur a demandé à être alerté : un bandeau qui
  // arrive deux minutes après l'évènement n'a plus grand intérêt.
  const wantsAlerts = usePreferencesStore((state) => state.osNotifications || state.browserNotifications);
  const notifications = useNotifications({ onlyUnread: true }, { refetchInterval: wantsAlerts ? 30_000 : 120_000 });
  useNotificationAlerts(notifications.data?.page.items);
  const subscription = useSubscription();

  const permissions = user?.permissions ?? [];
  const unreadCount = notifications.data?.unreadCount ?? 0;
  const routerState = useRouterState({ select: (state) => state.location.pathname });
  const breadcrumbLabel = useBreadcrumbLabel(routerState);

  // Tiroir de navigation mobile : fermé par défaut, la barre latérale fixe ne s'affichant
  // qu'à partir de `lg`. En dessous, c'est ce tiroir qui porte la navigation.
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  // Une navigation (clic sur un lien, bouton précédent du breadcrumb, retour navigateur)
  // referme le tiroir : sans ça, l'utilisateur atterrit sur le nouvel écran avec le menu
  // encore ouvert par-dessus. Ajustement en cours de rendu plutôt que dans un effet : évite
  // un rendu-effet-rendu pour une mise à jour qui ne synchronise rien d'externe.
  const [previousPathname, setPreviousPathname] = useState(routerState);
  if (routerState !== previousPathname) {
    setPreviousPathname(routerState);
    setIsMobileNavOpen(false);
  }

  // Le tiroir se superpose au contenu : sans ce verrou, la page defile derriere l'overlay,
  // ce qui desynchronise visuellement l'un de l'autre sur mobile.
  useEffect(() => {
    if (!isMobileNavOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsMobileNavOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMobileNavOpen]);

  return (
    <div className="flex min-h-dvh">
      {isMobileNavOpen ? (
        <div
          onClick={() => setIsMobileNavOpen(false)}
          aria-hidden
          className="fixed inset-0 z-40 bg-ink-900/40 backdrop-blur-sm lg:hidden"
        />
      ) : null}

      {/* Barre latérale : tiroir superposé sous `lg` (masqué hors écran par défaut, poussé par
          `isMobileNavOpen`), repositionnée en colonne statique à partir de `lg`. */}
      <aside
        id="app-sidebar"
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-72 -translate-x-full flex-col gap-6 overflow-y-auto border-r border-ink-100/70 bg-white px-4 py-6 shadow-xl transition-transform duration-200 ease-out',
          'lg:static lg:z-auto lg:w-64 lg:shrink-0 lg:translate-x-0 lg:bg-white/70 lg:shadow-none lg:backdrop-blur-sm',
          isMobileNavOpen && 'translate-x-0',
        )}
      >
        <div className="flex items-center justify-between px-3">
          <PixlideLogo />

          <button
            type="button"
            onClick={() => setIsMobileNavOpen(false)}
            className="grid size-9 place-items-center rounded-pill text-ink-500 transition hover:bg-ink-50 hover:text-ink-900 lg:hidden"
            aria-label="Fermer le menu"
          >
            <X aria-hidden className="size-5" />
          </button>
        </div>

        <nav aria-label="Navigation principale" className="flex flex-1 flex-col gap-6">
          <NavList entries={primaryNav} permissions={permissions} onNavigate={() => setIsMobileNavOpen(false)} />

          <div className="border-t border-ink-100/70 pt-5">
            <NavList entries={secondaryNav} permissions={permissions} onNavigate={() => setIsMobileNavOpen(false)} />
          </div>
        </nav>

        {subscription.data ? <PlanUsageCard subscription={subscription.data} /> : null}
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center gap-4 px-6 py-5">
          <button
            type="button"
            onClick={() => setIsMobileNavOpen(true)}
            className="grid size-10 shrink-0 place-items-center rounded-pill bg-white/80 text-ink-500 ring-1 ring-ink-100 transition hover:text-ink-900 lg:hidden"
            aria-label="Ouvrir le menu"
            aria-expanded={isMobileNavOpen}
            aria-controls="app-sidebar"
          >
            <Menu aria-hidden className="size-5" />
          </button>

          {breadcrumbLabel ? (
            <button
              type="button"
              // Retourne à l'écran précédent plutôt qu'au tableau de bord : depuis
              // `/creer/template`, un retour doit ramener à `/creer`, pas sauter deux niveaux.
              onClick={() => {
                if (window.history.length > 1) {
                  router.history.back();
                } else {
                  void router.navigate({ to: '/' });
                }
              }}
              className="flex items-center gap-1.5 text-sm text-ink-500 hover:text-ink-900"
              aria-current="page"
            >
              <ArrowLeft aria-hidden className="size-4" />
              {breadcrumbLabel}
            </button>
          ) : null}

          <div className="ml-auto flex items-center gap-2">
            <div className="relative hidden md:block">
              <Search
                aria-hidden
                className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-300"
              />
              <input
                type="search"
                // Un `placeholder` ne remplace pas un libellé : il disparaît à la saisie et
                // n'est pas systématiquement annoncé. On fournit donc un aria-label.
                aria-label="Rechercher dans le compte"
                placeholder="Rechercher"
                className="h-10 w-56 rounded-pill bg-white/80 pr-4 pl-9 text-sm ring-1 ring-ink-100 placeholder:text-ink-300 focus:ring-brand-500"
              />
            </div>

            <Link
              to="/notifications"
              className="relative grid size-10 place-items-center rounded-pill bg-white/80 text-ink-500 ring-1 ring-ink-100 transition hover:text-ink-900"
              aria-label={
                unreadCount > 0
                  ? `Notifications, ${unreadCount} non lue${unreadCount > 1 ? 's' : ''}`
                  : 'Notifications'
              }
            >
              <Bell aria-hidden className="size-4.5" />
              {unreadCount > 0 ? (
                <span
                  aria-hidden
                  className="absolute -top-0.5 -right-0.5 grid size-5 place-items-center rounded-pill bg-brand-500 text-[10px] font-semibold text-white"
                >
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              ) : null}
            </Link>

            {user ? (
              <div className="flex items-center gap-2">
                <span
                  aria-hidden
                  className="grid size-10 place-items-center rounded-pill bg-brand-100 text-sm font-semibold text-brand-700"
                >
                  {user.initials}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => logout.mutate()}
                  isLoading={logout.isPending}
                >
                  Déconnexion
                </Button>
              </div>
            ) : null}
          </div>
        </header>

        {/* `key` sur le chemin : le focus et le défilement repartent du haut à chaque
            changement de page, comme le fait une navigation de document classique. */}
        <main key={routerState} className={cn('flex-1 px-6 pb-12')}>
          {children}
        </main>
      </div>

      <Toaster />
    </div>
  );
}
